export type MemoizedLoader<K, V> = {
  /** The resolved value for `key`, if a previous load has completed. */
  peek(key: K): V | undefined;
  /**
   * Resolves `key`, reusing a completed value or an in-flight load. A rejected
   * load is not cached, so the next call retries.
   */
  load(key: K): Promise<V>;
};

export function createMemoizedLoader<K, V>(
  loadValue: (key: K) => Promise<V>
): MemoizedLoader<K, V> {
  // Boxed so a legitimately `undefined` value still counts as loaded.
  const loaded = new Map<K, { value: V }>();
  const pending = new Map<K, Promise<V>>();

  return {
    peek: (key) => loaded.get(key)?.value,
    load(key) {
      const cached = loaded.get(key);
      if (cached) return Promise.resolve(cached.value);

      const existing = pending.get(key);
      if (existing) return existing;

      const load = Promise.resolve()
        .then(() => loadValue(key))
        .then((value) => {
          loaded.set(key, { value });
          return value;
        })
        .finally(() => {
          pending.delete(key);
        });

      pending.set(key, load);
      return load;
    },
  };
}
